/** Keep only nearby chunks (and a focused/open panel chunk) mounted. */
type Chunk = { node: HTMLDivElement; start: number; end: number; visible: boolean; mounted: boolean; dirty: boolean; height: number };
const SIZE = 20;
export const createOddsVirtualList = (content: HTMLElement, render: (host: HTMLElement, start: number, end: number) => void, after: () => void) => {
  let chunks: Chunk[] = [];
  let ids: string[] = [];
  let frame = 0;
  let disposed = false;
  const resize = new ResizeObserver(entries => {
    for (const entry of entries) {
      const chunk = chunks.find(value => value.node === entry.target);
      if (chunk?.mounted) chunk.height = Math.max(1, chunk.node.getBoundingClientRect().height);
    }
  });
  const schedule = () => {
    if (frame || disposed) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      if (!content.isConnected) { dispose(); return; }
      let changed = false;
      for (const chunk of chunks) {
        const retained = chunk.node.contains(document.activeElement) || Boolean(chunk.node.querySelector('[data-gwd-odds-panel]:not([hidden])'));
        if (chunk.visible || retained) {
          if (!chunk.mounted || chunk.dirty) {
            render(chunk.node, chunk.start, chunk.end);
            chunk.node.style.height = '';
            chunk.mounted = true; chunk.dirty = false;
            resize.observe(chunk.node);
            changed = true;
          }
        } else if (chunk.mounted) {
          chunk.height = Math.max(1, chunk.node.getBoundingClientRect().height);
          resize.unobserve(chunk.node);
          chunk.node.replaceChildren(); chunk.node.style.height = `${chunk.height}px`;
          chunk.mounted = false; changed = true;
        }
      }
      if (changed) after();
    });
  };
  const observer = new IntersectionObserver(entries => {
    for (const entry of entries) {
      const chunk = chunks.find(value => value.node === entry.target);
      if (chunk) chunk.visible = entry.isIntersecting;
    }
    schedule();
  }, { rootMargin: '1200px 0px' });
  const dispose = () => {
    disposed = true; cancelAnimationFrame(frame); observer.disconnect(); resize.disconnect();
    content.removeEventListener('focusout', schedule);
  };
  content.addEventListener('focusout', schedule);
  return {
    update(nextIds: string[]) {
      if (disposed) return;
      const same = ids.length === nextIds.length && ids.every((id, index) => id === nextIds[index]);
      if (!same) {
        observer.disconnect(); resize.disconnect();
        const previous = new Map(chunks.map(chunk => [ids.slice(chunk.start, chunk.end).join('\u0000'), chunk]));
        const average = chunks.filter(chunk => chunk.height > 1).reduce((sum, chunk) => sum + chunk.height / Math.max(1, chunk.end - chunk.start), 0) / Math.max(1, chunks.filter(chunk => chunk.height > 1).length);
        const rowHeight = average || 190;
        ids = nextIds.slice(); chunks = [];
        let reference: ChildNode | null = content.firstChild;
        for (let start = 0; start < ids.length; start += SIZE) {
          const end = Math.min(start + SIZE, ids.length);
          const existing = previous.get(ids.slice(start, end).join('\u0000'));
          if (existing) {
            existing.start = start; existing.end = end; existing.dirty = true;
            chunks.push(existing);
            if (existing.node !== reference) content.insertBefore(existing.node, reference);
            reference = existing.node.nextSibling;
            if (existing.mounted) resize.observe(existing.node);
            continue;
          }
          const node = document.createElement('div');
          node.dataset.gwdOddsChunk = ''; node.style.display = 'flow-root';
          const height = rowHeight * (end - start);
          node.style.height = `${height}px`;
          const chunk = { node, start, end, visible: start === 0, mounted: false, dirty: true, height };
          chunks.push(chunk); content.insertBefore(node, reference);
        }
        const retained = new Set(chunks.map(chunk => chunk.node));
        Array.from(content.children).forEach(node => { if (!retained.has(node as HTMLDivElement)) node.remove(); });
        chunks.forEach(chunk => observer.observe(chunk.node));
      } else chunks.forEach(chunk => { chunk.dirty = true; });
      schedule();
    },
    schedule,
    dispose,
    matches(nextIds: string[]) { return ids.length === nextIds.length && ids.every((id, index) => id === nextIds[index]); },
  };
};
